/**
 * FallbackRecipientSelect Component
 *
 * Step 3 of the approval flow: who gets the document when step 2 finds nobody — a fallback user,
 * the approver the creator picks, or nobody.
 */
import { InputWrapper, Select } from '@insio/ui-toolkit';
import type { Permission, UserRole } from '@insio/shared-types';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import {
  fallbackIssueOf,
  recipientOf,
  requiresUser,
  withRecipient,
  type FallbackDraft,
  type FallbackRecipient,
  type FallbackUser
} from './approval-flow';
import { FallbackIssueBanner } from './ChainStepFields';

/** Option values of the two answers without a fallback user; a user id never matches them. */
const OWNER_PICKS_VALUE = '__owner-picks__';
const NONE_VALUE = '__none__';

export interface FallbackRecipientSelectProps {
  /** Feature translation prefix of the `fallback` subtree. */
  translationPrefix: string;
  draft: FallbackDraft;
  onChange: (draft: FallbackDraft) => void;
  /** Everyone who may approve this kind; undefined until known, when no pick is flagged. */
  users: readonly FallbackUser[] | undefined;
  roles: readonly UserRole[];
  approvePermission: Permission;
  /** Locks the pick, e.g. while those who may approve are not known. */
  disabled?: boolean;
  testIdPrefix: string;
}

export function FallbackRecipientSelect({
  translationPrefix,
  draft,
  onChange,
  users,
  roles,
  approvePermission,
  disabled = false,
  testIdPrefix
}: FallbackRecipientSelectProps) {
  const t = useFeatureTranslation('systemSettings.automation.approvalFlow.recipient');
  const recipient = recipientOf(draft);
  const value =
    recipient === 'USER'
      ? draft.userId
      : recipient === 'OWNER_PICKS'
        ? OWNER_PICKS_VALUE
        : NONE_VALUE;
  // Under the user chain that user is step 2 already, so only the two fixed answers remain.
  const userOptions = requiresUser(draft.strategy)
    ? []
    : (users ?? []).map(user => ({
        value: user.id,
        label: user.displayName,
        description: t('userDescription')
      }));
  const issue = fallbackIssueOf(draft, { users, eligibleRoles: roles, approvePermission });

  const handleChange = (picked: string) => {
    const next: FallbackRecipient =
      picked === OWNER_PICKS_VALUE ? 'OWNER_PICKS' : picked === NONE_VALUE ? 'NONE' : 'USER';
    onChange(withRecipient(draft, next, picked));
  };

  return (
    <div className='flex flex-col gap-md'>
      <InputWrapper label={t('label')} className='gap-xs lg:w-96'>
        <Select
          value={value}
          onChange={picked => handleChange(String(picked))}
          options={[
            ...userOptions,
            {
              value: OWNER_PICKS_VALUE,
              label: t('OWNER_PICKS'),
              description: t('fixedDescription')
            },
            { value: NONE_VALUE, label: t('NONE'), description: t('fixedDescription') }
          ]}
          isSearchable={userOptions.length > 0}
          disabled={disabled}
          testId={`${testIdPrefix}-recipient`}
        />
      </InputWrapper>

      {issue?.kind === 'USER_CANNOT_APPROVE' && recipient === 'USER' && (
        <FallbackIssueBanner
          translationPrefix={translationPrefix}
          issue={issue}
          testIdPrefix={testIdPrefix}
        />
      )}
    </div>
  );
}
